import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import lckData from '../data/lck.json';
import etoilesData from '../data/etoiles.json';
import EquipeModel from '../models/equipe';
import Header from '../widgets/molecules/header';
import EquipeClassement from '../widgets/atomes/equipeClassement';
import ListeClassement from '../widgets/molecules/listeClassement';
import ListeCalendar from '../widgets/molecules/listeCalendar';
import DayMatches from '../widgets/molecules/dayMatches';
import MatchCalendar from '../widgets/atomes/matchCalendar';
import VideoIcon from "../widgets/icons/video";
import BoutonIcon from "../widgets/atomes/boutonIcon";
import Overlay from "../widgets/molecules/overlay";
import { fetchLCKMatches, computeStandings } from '../services/lckApi';

function LCKPage() {
    const navigate = useNavigate();
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [filterT1, setFilterT1] = useState(false);
    const [openSection, setOpenSection] = useState('classement');
    const toggle = (section) => setOpenSection(s => s === section ? null : section);
    const [matches, setMatches] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const scrollContainerRef = useRef(null);
    const dayRefs = useRef({});
    const liens = etoilesData.etoiles.map(e => e.lien);
    const equipes = lckData.equipes.map(e => EquipeModel.fromJSON(e));

    const openOverlay = () => setIsOverlayOpen(true);

    useEffect(() => {
        fetchLCKMatches()
            .then(data => {
                setMatches(data);
                setIsLoading(false);
            })
            .catch(err => {
                setError(err.message);
                setIsLoading(false);
            });
    }, []);

    const scrollToToday = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayStr = today.toISOString().split('T')[0];
        const sortedDays = Object.keys(dayRefs.current).sort();
        const targetDay = sortedDays.find(d => d >= todayStr);
        if (!targetDay || !dayRefs.current[targetDay]) return;
        const targetEl = dayRefs.current[targetDay];
        const isMobile = window.innerWidth < 768;
        if (!isMobile && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = targetEl.offsetTop;
        } else {
            targetEl.scrollIntoView({ block: 'start' });
        }
    };

    useEffect(() => {
        if (isLoading) return;
        const isMobile = window.innerWidth < 768;
        if (!isMobile) setTimeout(scrollToToday, 0);
    }, [isLoading]);

    useEffect(() => {
        if (openSection !== 'calendrier' || isLoading) return;
        setTimeout(scrollToToday, 50);
    }, [openSection, isLoading]);

    const equipesTries = computeStandings(equipes, matches)
        .sort((a, b) => {
            const diff = (b.wins - b.losses) - (a.wins - a.losses);
            if (diff !== 0) return diff;
            const gpDiff = b.gamePoints - a.gamePoints;
            return gpDiff !== 0 ? gpDiff : a.name.localeCompare(b.name);
        });

    let upcomingMatches = filterT1
        ? matches.filter(m => m.team1 === 'T1' || m.team2 === 'T1')
        : matches;

    const matchesByDay = upcomingMatches.reduce((acc, match) => {
        if (!acc[match.date]) acc[match.date] = [];
        acc[match.date].push(match);
        return acc;
    }, {});

    const sortedDays = Object.keys(matchesByDay).sort();

    return (
        <>
            <div className="bg-image"></div>
            <div className="flex flex-col min-h-screen md:h-screen text-white relative">
                <Header etoiles={liens}/>
                <div className="bg-[#1A1A1A] bg-opacity-70 backdrop-blur-sm border-b border-gray-700 px-4 md:px-8 py-4 md:py-6">
                    <div className="flex items-center justify-between gap-3 md:gap-6">
                        <button
                            onClick={() => navigate('/')}
                            className="flex items-center gap-2 hover:opacity-80 transition-opacity flex-shrink-0"
                        >
                            <span className="text-3xl md:text-4xl">←</span>
                        </button>

                        <div className="flex-1 text-center">
                            <h1 className="text-lg md:text-2xl font-bold font-poppins">LCK - Rounds 1/2</h1>
                        </div>
                        <BoutonIcon
                            icon={<VideoIcon/>}
                            onClick={(e) => {
                                e.stopPropagation();
                                openOverlay();
                            }}
                        />
                        <button
                            onClick={() => setFilterT1(!filterT1)}
                            className={`px-3 md:px-4 py-2 rounded text-xs md:text-sm font-semibold transition-all flex-shrink-0 ${
                                filterT1
                                    ? 'bg-red-600 text-white'
                                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                        >
                            <span className="hidden sm:inline">Afficher les matchs de </span>T1
                        </button>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row md:flex-1 gap-4 md:gap-8 px-4 md:px-8 pt-4 md:pt-8 md:overflow-hidden w-full">
                    <div className="flex-1 md:overflow-hidden">
                        <div
                            className="flex justify-between items-center cursor-pointer md:hidden bg-[#272727] rounded-lg px-4 py-3 mb-2 sticky top-0 z-10"
                            onClick={() => toggle('calendrier')}
                        >
                            <span className="text-white font-semibold text-xl font-poppins">Calendrier</span>
                            <span className="text-gray-400 text-lg select-none">{openSection === 'calendrier' ? '▲' : '▼'}</span>
                        </div>
                        <div ref={scrollContainerRef} className={`md:h-full overflow-y-auto pb-8 ${openSection === 'calendrier' ? '' : 'hidden md:block'}`}>
                            {isLoading && (
                                <div className="flex items-center justify-center h-full text-gray-400">
                                    Chargement des matchs...
                                </div>
                            )}
                            {error && (
                                <div className="flex items-center justify-center h-full text-red-400">
                                    Erreur : {error}
                                </div>
                            )}
                            {!isLoading && !error && (
                                <ListeCalendar>
                                    {sortedDays.map((day) => (
                                        <div
                                            key={day}
                                            ref={(el) => { if (el) dayRefs.current[day] = el; }}
                                        >
                                            <DayMatches
                                                dateStr={day}
                                                matches={
                                                    <div className="flex flex-col gap-4">
                                                        {matchesByDay[day].map((match) => (
                                                            <MatchCalendar
                                                                key={match.id}
                                                                match={match}
                                                                equipes={lckData.equipes}
                                                            />
                                                        ))}
                                                    </div>
                                                }
                                                equipes={lckData.equipes}
                                            />
                                        </div>
                                    ))}
                                </ListeCalendar>
                            )}
                        </div>
                    </div>

                    <div className="w-full md:w-[30%] md:overflow-hidden">
                        <div
                            className="flex justify-between items-center cursor-pointer md:hidden bg-[#272727] rounded-lg px-4 py-3 mb-2 sticky top-0 z-10"
                            onClick={() => toggle('classement')}
                        >
                            <span className="text-white font-semibold text-xl font-poppins">Classement</span>
                            <span className="text-gray-400 text-lg select-none">{openSection === 'classement' ? '▲' : '▼'}</span>
                        </div>
                        <div className={`md:h-full overflow-y-auto py-4 md:py-6 ${openSection === 'classement' ? '' : 'hidden md:block'}`}>
                            <ListeClassement>
                                {equipesTries.map((equipe, index) => (
                                    <EquipeClassement
                                        key={equipe.id}
                                        equipe={equipe}
                                        classement={index + 1}
                                    />
                                ))}
                            </ListeClassement>
                        </div>
                    </div>
                </div>
            </div>
            {isOverlayOpen && (
                <Overlay onClose={() => setIsOverlayOpen(false)}>
                    <iframe
                        src={`https://www.youtube.com/embed/SE7ntM4Om6A?autoplay=1`}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-[90vw] h-[90vh] max-w-[1600px] max-h-[900px] rounded-md"
                    />
                </Overlay>
            )}
        </>
    );
}

export default LCKPage;
